import Emitter from "@homer.core/emitter";
import Channel from "@homer.core/channel";
import deepobjectdiff from "@homer.adapter/deepobjectdiff";
import scheduler from "@homer.core/scheduler";
import StorageCookie from "@homer.core/storage.cookie";
import StorageLocal from "@homer.core/storage.local";
import homer from "@homer.api/homer";
import user from "@homer.core/user";
import page from "@homer.core/page";
import application from "@homer.core/application";
import { name } from "../package.json";

const channel = Channel.obtain(name);
const classes = document.documentElement.classList;
const storageCookie = new StorageCookie(name, { version: application.version });
const storageLocal = new StorageLocal(name, { version: application.version });
const flag = "hasCart";
const state = {
    current: Object.freeze(JSON.parse(storageLocal.get() || "null")),
    previous: Object.freeze(null),
};

if (!state.current) {
    storageCookie.remove();
}

class Cart extends Emitter {
    constructor() {
        super();

        Promise.resolve(user).then((user) => {
            user.on("change", (event, current, previous, details, isExternal = false) => {
                if (isExternal) {
                    return;
                }

                void this.retrieve();
            });
        });

        this.on(/^((?!(change)).)+$/, (event, current, previous, search, isExternal = false) => {
            scheduler.yield.medium().then(() => {
                if (current && current?.items?.length) {
                    classes.add(flag);
                } else {
                    classes.remove(flag);
                }
            });

            if (!deepobjectdiff.isEqual(previous, current)) {
                state.previous = previous;
                state.current = current;

                scheduler.yield.medium().then(() => {
                    storageLocal.set(JSON.stringify(current));

                    if (current && current.quantity) {
                        storageCookie.set(current.quantity);
                    } else {
                        storageCookie.remove();
                    }

                    this.emit("change", current, previous, search, isExternal);
                });
            }

            if (!isExternal) {
                channel.broadcast({ id: event.name, current, previous, search });
            }
        });

        channel.on("broadcast", (event, payload, isExternal) => {
            if (isExternal) {
                const { id, current, previous, search } = payload;

                this.emit(id, current, previous, search, true);
            }
        });

        if (page.type === "cart") {
            const url = URL.resolve(location.href);
            const hash = url.parameter?.cart?.restore;

            if (hash) {
                url.searchParams.delete("cart[restore]");
                page.replace(url.href);

                return this.restore(hash).catch(() => {
                    return this.retrieve();
                });
            }
        }

        if (state.current) {
            void this.retrieve();

            return this;
        }

        return this.retrieve();
    }

    get current() {
        return state.current;
    }

    get previous() {
        return state.previous;
    }

    retrieve() {
        return homer.client.post("/cart/retrieve", { source: page.type }).then((cart) => {
            this.emit("retrieve", Object.freeze(cart), state.current, null);

            return this;
        });
    }

    restore(hash) {
        return homer.client
            .post(
                "/cart/restore",
                { hash, source: page.type },
                homer.Request.flag.attachToken | homer.Request.flag.attachChallenge,
            )
            .then((cart) => {
                this.emit("retrieve", Object.freeze(cart), state.current, null);

                return this;
            })
            .finally(() => {
                void user.retrieve();
            });
    }

    delta(previousTemp, currentTemp) {
        const result = {
            changes: 0,
            added: [],
            removed: [],
        };

        if (!previousTemp) return result;

        const previous = Object.fromEntries(previousTemp.map((item) => [item.increment, item]));
        const current = Object.fromEntries(currentTemp.map((item) => [item.increment, item]));

        const previousKeys = Object.keys(previous);
        const currentKeys = Object.keys(current);

        // added products
        let incrementsAdded = currentKeys.filter((x) => !previousKeys.includes(x));
        result.added = result.added.concat(incrementsAdded.map((item) => current[item]));

        // removed products
        let incrementsRemoved = previousKeys.filter((x) => !currentKeys.includes(x));
        result.removed = result.removed.concat(incrementsRemoved.map((item) => previous[item]));

        // quantity changes
        for (const i in current) {
            const cur = current[i];
            const prv = previous[i];
            if (!prv) continue;

            if (cur.quantity === prv.quantity) continue;

            const item = {
                ...cur,
                quantity: Math.abs(cur.quantity - prv.quantity),
                price_total: { offer: Math.abs(cur.price_total.offer - prv.price_total.offer) },
            };

            if (cur.quantity > prv.quantity) {
                result.added.push(item);
            } else if (cur.quantity < prv.quantity) {
                result.removed.push(item);
            }
        }

        result.changes = result.added.length + result.removed.length;

        return result;
    }
}

export default new Cart();
